// Formulário de cartão de crédito (Pagar.me / Stone).
//
// A tokenização acontece AQUI, no navegador, direto na API do Pagar.me usando a
// CHAVE PÚBLICA (pk_). O número do cartão NUNCA passa pelo nosso backend — o que
// sai daqui é só um `cardToken` de vida curta. O pai recebe (token, installments,
// billing) em onPagar() e chama a API de cobrança (createCardPaymentApi).
//
// O ENDEREÇO DE COBRANÇA (billing) é obrigatório no cartão: o token não carrega
// endereço, então o Pagar.me exige informá-lo separadamente na cobrança.
import { useEffect, useState } from 'react';
import { CreditCard, Lock } from 'lucide-react';
import { Field } from './Fields';
import { notify } from './Notify';
import { buscarEnderecoPorCepApi } from '../api';
import './CardForm.css';

const soDigitos = (v) => String(v || '').replace(/\D/g, '');
const maskNumero = (v) => soDigitos(v).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ').trim();
const maskValidade = (v) => {
    const d = soDigitos(v).slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};
const maskCep = (v) => { const d = soDigitos(v).slice(0, 8); return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d; };
const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

export default function CardForm({ publicKey, botaoLabel = 'Pagar no cartão', processando = false, disabled = false, onPagar, enderecoPadrao = null }) {
    const [numero, setNumero] = useState('');
    const [nome, setNome] = useState('');
    const [validade, setValidade] = useState('');
    const [cvv, setCvv] = useState('');
    const [tokenizando, setTokenizando] = useState(false);

    // Endereço de cobrança
    const [end, setEnd] = useState({ cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' });
    const [buscandoCep, setBuscandoCep] = useState(false);

    // Prefill (ex.: reaproveitar o endereço de entrega já digitado na física).
    useEffect(() => {
        if (!enderecoPadrao) return;
        setEnd((p) => ({
            ...p,
            cep: maskCep(enderecoPadrao.cep || ''),
            logradouro: enderecoPadrao.logradouro || '',
            numero: enderecoPadrao.numero || '',
            complemento: enderecoPadrao.complemento || '',
            bairro: enderecoPadrao.bairro || '',
            cidade: enderecoPadrao.cidade || '',
            uf: (enderecoPadrao.uf || '').toUpperCase(),
        }));
    }, [enderecoPadrao]);

    const setE = (campo) => (e) => setEnd((p) => ({ ...p, [campo]: e.target.value }));

    const ocupado = processando || tokenizando;

    const buscarCep = async () => {
        const cep = soDigitos(end.cep);
        if (cep.length !== 8) return;
        setBuscandoCep(true);
        try {
            const v = await buscarEnderecoPorCepApi({ cep });
            if (v) {
                setEnd((p) => ({
                    ...p,
                    logradouro: v.logradouro || p.logradouro,
                    bairro: v.bairro || p.bairro,
                    cidade: v.localidade || p.cidade,
                    uf: (v.uf || p.uf).toUpperCase(),
                }));
            }
        } finally {
            setBuscandoCep(false);
        }
    };

    const validar = () => {
        const num = soDigitos(numero);
        if (num.length < 13) return 'Número do cartão inválido.';
        if (!nome.trim() || !nome.trim().includes(' ')) return 'Informe o nome completo como está no cartão.';
        const val = soDigitos(validade);
        if (val.length !== 4) return 'Validade inválida. Use MM/AA.';
        const mes = Number(val.slice(0, 2));
        if (mes < 1 || mes > 12) return 'Mês de validade inválido.';
        if (soDigitos(cvv).length < 3) return 'CVV inválido.';
        // Endereço de cobrança (obrigatório no cartão — o Pagar.me monta o line_1
        // com logradouro/número/bairro; sem eles o antifraude tende a recusar).
        if (soDigitos(end.cep).length !== 8) return 'Informe o CEP de cobrança (8 dígitos).';
        if (!end.logradouro.trim()) return 'Informe o logradouro (rua) do endereço de cobrança.';
        if (!end.numero.trim()) return 'Informe o número do endereço de cobrança.';
        if (!end.bairro.trim()) return 'Informe o bairro do endereço de cobrança.';
        if (!end.cidade.trim() || !end.uf.trim()) return 'Endereço de cobrança incompleto (cidade/UF).';
        return null;
    };

    const pagar = async () => {
        if (ocupado || disabled) return;
        if (!publicKey) return notify('Pagamento por cartão indisponível no momento.');
        const erro = validar();
        if (erro) return notify(erro);

        setTokenizando(true);
        try {
            const val = soDigitos(validade);
            const resp = await fetch(
                `https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(publicKey)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'card',
                        card: {
                            number: soDigitos(numero),
                            holder_name: nome.trim(),
                            exp_month: Number(val.slice(0, 2)),
                            exp_year: Number(val.slice(2)),
                            cvv: soDigitos(cvv),
                        },
                    }),
                }
            );
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || !data?.id) {
                const msg = data?.message
                    || (data?.errors && Object.values(data.errors).flat().join(' '))
                    || 'Não foi possível validar o cartão. Confira os dados.';
                throw new Error(msg);
            }
            // Monta o endereço de cobrança no formato do Pagar.me.
            const billing = {
                line_1: [end.numero.trim(), end.logradouro.trim(), end.bairro.trim()].filter(Boolean).join(', '),
                line_2: end.complemento.trim() || undefined,
                zip_code: soDigitos(end.cep),
                city: end.cidade.trim(),
                state: end.uf.trim().toUpperCase(),
                country: 'BR',
            };
            await onPagar(data.id, 1, billing);
        } catch (e) {
            notify(e?.message || 'Falha ao validar o cartão. Confira os dados e tente novamente.');
        } finally {
            setTokenizando(false);
        }
    };

    return (
        <div className="ueb-card-form">
            <Field label="Número do cartão" htmlFor="cc-num">
                <input id="cc-num" className="pinput" inputMode="numeric" autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000" value={numero}
                    onChange={(e) => setNumero(maskNumero(e.target.value))} />
            </Field>

            <Field label="Nome impresso no cartão" htmlFor="cc-name">
                <input id="cc-name" className="pinput" autoComplete="cc-name"
                    placeholder="Como está no cartão" value={nome}
                    onChange={(e) => setNome(e.target.value.toUpperCase())} />
            </Field>

            <div className="ueb-card-row">
                <Field label="Validade" htmlFor="cc-exp">
                    <input id="cc-exp" className="pinput" inputMode="numeric" autoComplete="cc-exp"
                        placeholder="MM/AA" value={validade}
                        onChange={(e) => setValidade(maskValidade(e.target.value))} />
                </Field>
                <Field label="CVV" htmlFor="cc-cvv">
                    <input id="cc-cvv" className="pinput" inputMode="numeric" autoComplete="cc-csc"
                        placeholder="000" value={cvv}
                        onChange={(e) => setCvv(soDigitos(e.target.value).slice(0, 4))} />
                </Field>
            </div>

            <p className="ueb-card-sub">Endereço de cobrança do cartão</p>

            <div className="ueb-card-row">
                <Field label="CEP" htmlFor="cc-cep">
                    <input id="cc-cep" className="pinput" inputMode="numeric" placeholder="00000-000"
                        value={end.cep}
                        onChange={(e) => setEnd((p) => ({ ...p, cep: maskCep(e.target.value) }))}
                        onBlur={buscarCep} />
                </Field>
                <Field label="Número" htmlFor="cc-num-end">
                    <input id="cc-num-end" className="pinput" inputMode="numeric" placeholder="Nº"
                        value={end.numero} onChange={setE('numero')} />
                </Field>
            </div>

            <Field label="Endereço" htmlFor="cc-log">
                <input id="cc-log" className="pinput" placeholder={buscandoCep ? 'Buscando…' : 'Rua / logradouro'}
                    value={end.logradouro} onChange={setE('logradouro')} />
            </Field>

            <div className="ueb-card-row">
                <Field label="Bairro" htmlFor="cc-bairro">
                    <input id="cc-bairro" className="pinput" value={end.bairro} onChange={setE('bairro')} />
                </Field>
                <Field label="Complemento" htmlFor="cc-comp">
                    <input id="cc-comp" className="pinput" placeholder="Opcional" value={end.complemento} onChange={setE('complemento')} />
                </Field>
            </div>

            <div className="ueb-card-row">
                <Field label="Cidade" htmlFor="cc-cidade">
                    <input id="cc-cidade" className="pinput" value={end.cidade} onChange={setE('cidade')} />
                </Field>
                <Field label="UF" htmlFor="cc-uf">
                    <select id="cc-uf" className="pselect" value={end.uf} onChange={setE('uf')}>
                        <option value="">--</option>
                        {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                </Field>
            </div>

            <button type="button" className="pbtn pbtn--primary pbtn--block pbtn--lg"
                onClick={pagar} disabled={ocupado || disabled}>
                {ocupado ? <span className="pspin" /> : <><CreditCard size={18} /> {botaoLabel}</>}
            </button>

            <p className="phint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                <Lock size={13} /> Seus dados são enviados com segurança direto ao Pagar.me. O cartão não passa pelo nosso servidor.
            </p>
        </div>
    );
}
